import {
  Card,
  Empty,
  Flex,
  Loading,
  Select,
  type SelectProps,
  Space,
  Text,
  theme,
} from '@/components';
import { getPinpointSheetType } from '@/features/comment/utils/pinpoint';
import { useProject } from '@/features/project/_hooks/project';
import { useProjectDetailSearchQueryStates } from '@/features/project/_hooks/search-query-states';
import { INDICATOR_ID_SEPARATOR } from '@/features/project/_utils/activity';
import { useActivityTreeOptions } from '@/features/project/projects/detail/activities/_hooks/activity';
import { useId } from '@/hooks';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectedActivityFlowRouteTable } from './SelectedActivityFlowRouteTable';

const ALL_PATH_FROM_ACTIVITY = 'all';

const createRouteIndicatorId = (
  routeIndicators: {
    indicator: { id: string };
  }[],
) => {
  return routeIndicators
    .map((v) => v.indicator.id)
    .join(INDICATOR_ID_SEPARATOR);
};

export const ActivityFlowTable: FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useId();
  const { tree, selectOptions } = useActivityTreeOptions(id);
  const { data: project } = useProject(id);

  const [{ routeIndicatorIds }, setSearchQueryStates] =
    useProjectDetailSearchQueryStates();

  const hasAllPathFromActivity = useMemo(
    () => routeIndicatorIds?.some((v) => v === ALL_PATH_FROM_ACTIVITY),
    [routeIndicatorIds],
  );

  const options = useMemo<SelectProps['options']>(() => {
    return [
      {
        label: t('all-path-from-activity'),
        value: ALL_PATH_FROM_ACTIVITY,
      },
      ...selectOptions.map((v) => ({
        label: v.label,
        value: createRouteIndicatorId(v.routeIndicators),
        disabled: hasAllPathFromActivity,
      })),
    ];
  }, [selectOptions, hasAllPathFromActivity, t]);

  const selectedRouteIndicator = useMemo<string[]>(() => {
    if (hasAllPathFromActivity) {
      return [ALL_PATH_FROM_ACTIVITY];
    }
    const targets =
      routeIndicatorIds?.flatMap<string>((id) => {
        const allMatch = options?.find((v) => v.value === id);
        if (allMatch) {
          return id;
        }
        const startPartialMatchIds = options?.flatMap((option) =>
          option.value?.toString()?.startsWith(id)
            ? option.value.toString()
            : [],
        );

        if (!startPartialMatchIds?.length) {
          return [];
        }

        return startPartialMatchIds;
      }) ?? [];

    return targets;
  }, [routeIndicatorIds, options, hasAllPathFromActivity]);

  const selectActivityRoute = useMemo(() => {
    if (hasAllPathFromActivity) {
      return selectOptions ?? [];
    }
    return (
      selectedRouteIndicator?.flatMap((value) => {
        const selectedRoute = selectOptions.find(
          (option) => createRouteIndicatorId(option.routeIndicators) === value,
        );
        return selectedRoute ? selectedRoute : [];
      }) ?? []
    );
  }, [selectedRouteIndicator, selectOptions, hasAllPathFromActivity]);

  if (tree.isPending || !tree.data) {
    return <Loading />;
  }

  const pinpointSheetType = getPinpointSheetType(
    project?.sheetType ?? 'RS',
    project?.fundAdditionalAttributes?.fundSheetType,
  );

  return (
    <Flex vertical gap={16}>
      <Space>
        <Text weight="regular" text={t('expression-path-from-activity')} />
        {selectOptions.length > 0 && (
          <Select
            placeholder={t('select-activity-flow')}
            options={options}
            value={
              selectedRouteIndicator.length
                ? selectedRouteIndicator
                : routeIndicatorIds
            }
            mode="multiple"
            style={{ width: 'fit-content', minWidth: 254 }}
            allowClear
            labelRender={({ value, label }) => {
              const target = selectedRouteIndicator.find((v) => v === value);
              if (!target) {
                return t('activity-flow.not-exist-in-option');
              }
              return label;
            }}
            onChange={(values, _options) => {
              const hasAllPathFromActivity = values.includes(
                ALL_PATH_FROM_ACTIVITY,
              );
              setSearchQueryStates((v) => {
                return {
                  ...v,
                  routeIndicatorIds: hasAllPathFromActivity
                    ? [ALL_PATH_FROM_ACTIVITY]
                    : values,
                };
              });
            }}
          />
        )}
      </Space>

      {selectOptions.length === 0 ? (
        <Empty />
      ) : selectActivityRoute?.length && project ? (
        <>
          {selectActivityRoute.map((route) => {
            const routeIndicatorId = createRouteIndicatorId(
              route.routeIndicators,
            );
            return (
              <Card
                key={routeIndicatorId}
                title={`${t('expression-path-from-activity')} ${route.label}`}
              >
                <SelectedActivityFlowRouteTable
                  routeIndicatorId={routeIndicatorId}
                  rootActivity={route.rootActivity}
                  routeIndicators={route.routeIndicators.map(
                    (routeIndicator) => {
                      const currentEdge = tree.data?.edges.find(
                        (edge) => edge.id === routeIndicator.path.fromEdgeId,
                      );
                      return {
                        indicator: routeIndicator.indicator,
                        edge: currentEdge,
                      };
                    },
                  )}
                  pinpointSheetType={pinpointSheetType}
                />
              </Card>
            );
          })}
        </>
      ) : (
        <div
          style={{
            width: '100%',
            height: 104,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: token.colorBgLayout,
          }}
        >
          <Text
            variant="h5"
            color="tertiary"
            text={t('message-select-expression-path-from-activity')}
          />
        </div>
      )}
    </Flex>
  );
};
